import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ChartCard } from '../../components/charts/ChartCard.jsx';
import { DonutChart } from '../../components/charts/DonutChart.jsx';
import { useChartPalette } from '../../components/charts/useChartPalette.js';
import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { CsvExportButton } from '../../components/data/CsvExportButton.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';
import { formatCurrency, formatDate } from '../../utils/formatters.js';

import { InvoiceCreateModal } from './InvoiceCreateModal.jsx';
import { PayInvoiceModal } from './PayInvoiceModal.jsx';

const CSV_COLUMNS = [
  { key: 'invoiceNumber', label: 'InvoiceNumber' },
  { key: 'type', label: 'Type' },
  { key: 'invoiceDate', label: 'InvoiceDate' },
  { key: 'dueDate', label: 'DueDate' },
  { key: 'totalAmount', label: 'Total' },
  { key: 'paidAmount', label: 'Paid' },
  { key: 'currency', label: 'Currency' },
  { key: 'status', label: 'Status' },
];

export function InvoicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const palette = useChartPalette();
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [confirm, setConfirm] = useState(null);

  /* Die Hülle bleibt erhalten, nicht nur die Zeilen. `meta.total` ist die
     einzige Stelle, an der steht, wie viele Rechnungen es WIRKLICH gibt - und
     die Kennzahlen unten summieren über die geholten Zeilen. Ohne diesen
     Vergleich sähe eine abgeschnittene Liste aus wie eine vollständige, und
     "Offener Betrag" wäre eine Summe über eine Seite. */
  const { data: antwort, isLoading, error } = useQuery({
    queryKey: ['finance', 'invoices', { typeFilter, statusFilter }],
    queryFn: () =>
      financeApi.listInvoices({ type: typeFilter || undefined, status: statusFilter || undefined }),
  });
  const data = antwort?.items || [];
  const meta = antwort?.meta;

  const stats = useMemo(() => {
    const rows = data || [];
    const order = ['DRAFT', 'POSTED', 'PAID', 'CANCELLED'];
    // Amounts (CHF), not counts - the ring is weighted by money.
    const byStatusAmount = { DRAFT: 0, POSTED: 0, PAID: 0, CANCELLED: 0 };
    let outstanding = 0;
    let overdue = 0;
    let openCount = 0;
    let income = 0;
    let expense = 0;
    let cancelled = 0;
    let draft = 0;
    const now = new Date();
    for (const r of rows) {
      // Sums mix currencies - normalize each row to CHF at its booked rate so
      // the insight totals stay a single CHF figure (formatCurrency below pins
      // 'CHF' explicitly; the global display currency must not relabel these).
      const rate = Number(r.exchangeRate || 1);
      const amt = Number(r.totalAmount || 0) * rate;
      if (byStatusAmount[r.status] !== undefined) byStatusAmount[r.status] += amt;
      if (r.status === 'CANCELLED') {
        cancelled += amt;
      } else if (r.status === 'DRAFT') {
        draft += amt;
      } else if (r.type === 'SALES') {
        income += amt;
      } else if (r.type === 'PURCHASE') {
        expense += amt;
      }
      const open = Math.max(
        0,
        Number(r.totalAmount || 0) - Number(r.paidAmount || 0) - Number(r.creditedAmount || 0),
      ) * rate;
      if (r.status === 'POSTED' && open > 0.0001) {
        outstanding += open;
        openCount += 1;
        if (r.dueDate && new Date(r.dueDate) < now) overdue += open;
      }
    }
    return { order, byStatusAmount, outstanding, overdue, openCount, income, expense, cancelled, draft };
  }, [data]);

  // "All types" → income (green) / expenses (red) / cancelled (grey) / draft (blue).
  // A specific type → status distribution, cancelled also in grey (no longer red).
  const donut = !typeFilter
    ? {
        labels: [
          t('finance.invoices.cat.income'),
          t('finance.invoices.cat.expense'),
          t('status.CANCELLED'),
          t('status.DRAFT'),
        ],
        values: [stats.income, stats.expense, stats.cancelled, stats.draft],
        colors: [palette?.series?.[1], palette?.expense, palette?.axis, palette?.accent],
      }
    : {
        labels: stats.order.map((s) => t(`status.${s}`)),
        values: stats.order.map((s) => stats.byStatusAmount[s]),
        colors: [palette?.series?.[2], palette?.accent, palette?.series?.[1], palette?.axis],
      };

  const postMutation = useMutation({
    mutationFn: (id) => financeApi.postInvoice(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] }),
  });

  const cancelMutation = useMutation({
    mutationFn: (id) => financeApi.cancelInvoice(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => financeApi.deleteInvoice(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['finance', 'invoices'] }),
  });

  const columns = [
    {
      key: 'invoiceNumber',
      label: t('col.number'),
      render: (r) => <Link to={`/finance/invoices/${r.id}`}>{r.invoiceNumber}</Link>,
    },
    {
      key: 'type',
      label: t('col.type'),
      render: (r) => <Badge variant="info">{t(`invoiceType.${r.type}`)}</Badge>,
    },
    { key: 'invoiceDate', label: t('col.date'), render: (r) => formatDate(r.invoiceDate) },
    { key: 'dueDate',     label: t('col.due'),  render: (r) => formatDate(r.dueDate) },
    { key: 'totalAmount', label: t('col.total'), align: 'right', render: (r) => formatCurrency(r.totalAmount, r.currency) },
    { key: 'paidAmount',  label: t('col.paid'),  align: 'right', render: (r) => formatCurrency(r.paidAmount, r.currency) },
    { key: 'status',      label: t('col.status'), render: (r) => <Badge status={r.status} /> },
    {
      key: 'actions',
      label: '',
      render: (r) => {
        const buttons = [];
        if (r.status === 'DRAFT') {
          buttons.push(
            <Button
              key="post"
              size="sm"
              variant="primary"
              onClick={() => postMutation.mutate(r.id)}
              disabled={postMutation.isPending}
            >
              {t('finance.invoices.action.post')}
            </Button>,
            <Button
              key="delete"
              size="sm"
              variant="danger"
              disabled={deleteMutation.isPending}
              onClick={() => setConfirm({ kind: 'delete', invoice: r })}
            >
              {t('finance.invoices.action.delete')}
            </Button>,
          );
        }
        if (r.status === 'POSTED') {
          if (Number(r.paidAmount) < Number(r.totalAmount)) {
            buttons.push(
              <Button key="pay" size="sm" variant="primary" onClick={() => setPayTarget(r)}>
                {t('finance.invoices.action.pay')}
              </Button>,
            );
          }
          if (Number(r.paidAmount) <= 0) {
            buttons.push(
              <Button
                key="cancel"
                size="sm"
                variant="danger"
                disabled={cancelMutation.isPending}
                onClick={() => setConfirm({ kind: 'cancel', invoice: r })}
              >
                {t('finance.invoices.action.cancel')}
              </Button>,
            );
          }
        }
        return buttons.length ? (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>{buttons}</div>
        ) : null;
      },
    },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('finance.invoices.title')}</h1>
          <p className="muted">{t('finance.invoices.subtitle')}</p>
        </div>
        <div className="page-actions">
          <CsvExportButton rows={data || []} columns={CSV_COLUMNS} filename="rechnungen" />
          <Button variant="primary" onClick={() => setCreateOpen(true)}>
            {t('finance.invoices.action.new')}
          </Button>
        </div>
      </header>

      {/* Die Kennzahlen summieren die GEHOLTEN Zeilen. Sind das nicht alle,
          ist jede Zahl darunter zu klein - und zwar auf eine Art, die man ihr
          nicht ansieht. Die Fusszeile der Tabelle sagt es auch, aber die
          steht unter den Kennzahlen; wer nur hier hinsieht, muss es hier
          lesen. */}
      {meta?.total > data.length && (
        <div className="alert warning">
          {t('insight.gekuerzt', { gezeigt: data.length, gesamt: meta.total })}
        </div>
      )}

      {(data || []).length > 0 && (
        <div className="dashboard-grid">
          <ChartCard title={t('finance.invoices.insight.status')}>
            <DonutChart
              legend
              height={200}
              labels={donut.labels}
              values={donut.values}
              colors={donut.colors}
              formatValue={(n) => formatCurrency(n, 'CHF')}
            />
          </ChartCard>
          <div className="card-grid insight-metrics">
            <MetricCard label={t('finance.invoices.insight.open')} value={stats.openCount} />
            <MetricCard label={t('finance.invoices.insight.outstanding')} value={formatCurrency(stats.outstanding, 'CHF')} />
            <MetricCard label={t('finance.invoices.insight.overdue')} value={formatCurrency(stats.overdue, 'CHF')} />
            <MetricCard label={t('finance.invoices.insight.balance')} value={formatCurrency(stats.income - stats.expense, 'CHF')} />
          </div>
        </div>
      )}

      <div className="toolbar">
        <select className="select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="">{t('finance.invoices.filterType.all')}</option>
          <option value="SALES">{t('finance.invoices.filterType.sales')}</option>
          <option value="PURCHASE">{t('finance.invoices.filterType.purchase')}</option>
        </select>
        <select className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="">{t('finance.invoices.filterStatus.all')}</option>
          <option value="DRAFT">{t('status.DRAFT')}</option>
          <option value="POSTED">{t('status.POSTED')}</option>
          <option value="PAID">{t('status.PAID')}</option>
          <option value="CANCELLED">{t('status.CANCELLED')}</option>
        </select>
      </div>

      {error && <div className="alert danger">{error.message}</div>}
      {(cancelMutation.isError || deleteMutation.isError) && (
        <div className="alert danger">
          {(cancelMutation.error || deleteMutation.error)?.message || t('common.loadError')}
        </div>
      )}
      <Table columns={columns} rows={data} isLoading={isLoading} error={error} meta={meta} emptyMessage={t('empty.invoices')} onRowClick={(r) => navigate(`/finance/invoices/${r.id}`)} />

      <InvoiceCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <PayInvoiceModal invoice={payTarget} onClose={() => setPayTarget(null)} />

      <ConfirmDialog
        open={Boolean(confirm)}
        variant="danger"
        title={confirm?.kind === 'delete' ? t('finance.invoices.action.delete') : t('finance.invoices.action.cancel')}
        message={confirm?.kind === 'delete' ? t('finance.invoices.confirmDelete') : t('finance.invoices.confirmCancel')}
        confirmLabel={confirm?.kind === 'delete' ? t('finance.invoices.action.delete') : t('finance.invoices.action.cancel')}
        isPending={confirm?.kind === 'delete' ? deleteMutation.isPending : cancelMutation.isPending}
        onClose={() => setConfirm(null)}
        onConfirm={() => {
          if (!confirm) return;
          if (confirm.kind === 'delete') {
            deleteMutation.mutate(confirm.invoice.id);
          } else {
            cancelMutation.mutate(confirm.invoice.id);
          }
          setConfirm(null);
        }}
      />
    </section>
  );
}
